import { ChangeEvent, useState } from 'react';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';

import {
  Box,
  Button,
  Typography,
  IconButton,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

const PREFIX = 'ActivityTile';

const classes = {
  tileContainer: `${PREFIX}-container`,
  tileHeader: `${PREFIX}-header`,
  message: `${PREFIX}-message`,
  lastUpdate: `${PREFIX}-lastUpdate`,
  verifyLocation: `${PREFIX}-verifyLocation`,
  contactProvider: `${PREFIX}-contactProvider`,
  checkMapButton: `${PREFIX}-checkMapButton`,
  requestUpdateButton: `${PREFIX}-requestUpdateButton`,
  callProviderBox: `${PREFIX}-callProviderBox`,
  callProviderButton: `${PREFIX}-callProviderButton`,
  contactText: `${PREFIX}-contactText`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.tileContainer}`]: {
    border: '1px solid red',
    padding: theme.spacing(7),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  [`& .${classes.tileHeader}`]: {
    marginLeft: '20px',
  },
  [`& .${classes.message}`]: {
    marginLeft: theme.spacing(1),
    fontWeight: 'bold',
  },
  [`& .${classes.lastUpdate}`]: {
    marginBottom: theme.spacing(5),
    marginLeft: theme.spacing(8),
  },
  [`& .${classes.verifyLocation}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  [`& .${classes.contactProvider}`]: {
    marginLeft: theme.spacing(5),
    paddingLeft: '5px',
    paddingTop: '10px',
  },
  [`& .${classes.checkMapButton}`]: {
    alignSelf: 'flex-end',
    marginTop: '8px',
    width: '180px',
    height: '50px',
  },
  [`& .${classes.requestUpdateButton}`]: {
    marginRight: theme.spacing(1),
    width: '70%',
    height: '1px',
    backgroundColor: '#000',
    margin: '1px 0',
    marginBottom: '10px',
  },
  [`& .${classes.callProviderBox}`]: {
    marginRight: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    height: '100%',
    padding: '16px',
  },
  [`& .${classes.callProviderButton}`]: {
    width: '180px',
    height: '50px',
    marginBottom: '8px',
  },
  [`& .${classes.contactText}`]: {
    fontWeight: 'bold',
    marginTop: '20px',
  },
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '80%',
    maxHeight: '80vh',
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

interface ActivityTileProps {
  lastUpdate: string;
  tripLegId?: string;
  scenario: {
    action: string;
    solution: string;
  };
}

export default function ActivityTile({
  lastUpdate,
  tripLegId,
  scenario,
}: ActivityTileProps) {
  const t = useCommonTranslation();

  const [open, setOpen] = useState(false);
  const [driverLocation, setDriverLocation] = useState('yes');

  const handleCheckMapClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDriverLocation(event.target.value);
  };

  const handleSave = () => {
    setOpen(false);
  };

  return (
    <StyledBox className={classes.tileContainer}>
      <Box>
        <Box className={classes.tileHeader}>
          <Typography className={classes.message}>
            <IconButton className="info-icon">
              <Avatar src="/infoicon.svg" />
            </IconButton>
            {scenario.action}
          </Typography>
          <Typography className={classes.lastUpdate}>
            {t('monitoring.LastUpdateReceived')} {lastUpdate}.
          </Typography>
        </Box>

        <Box className={classes.verifyLocation}>
          <Box className={classes.requestUpdateButton} />
          <Typography className={classes.contactText}>
            {scenario.solution}
          </Typography>
          <Button
            variant="contained"
            className={classes.checkMapButton}
            onClick={handleCheckMapClick}>
            {t('monitoring.activitiesCheckMap')}
          </Button>
        </Box>
      </Box>

      <StyledDialog onClose={handleClose} open={open}>
        <DialogTitle>
          {scenario.action}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {/* <TripDetailMapTab /> */}
          <FormControl component="fieldset">
            <RadioGroup
              aria-label="driver location"
              name="driver-location"
              value={driverLocation}
              onChange={handleRadioChange}>
              <FormControlLabel
                value="yes"
                control={<Radio />}
                label={t('monitoring.appearYesPickupLocation')}
              />
              <FormControlLabel
                value="no"
                control={<Radio />}
                label={t('monitoring.appearNoPickupLocation')}
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button autoFocus color="primary" size="large" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSave}>
            {t('monitoring.activitiesSaveDialog')}
          </Button>
        </DialogActions>
      </StyledDialog>
    </StyledBox>
  );
}
